import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { monthlySalary, WEEKS_PER_MONTH, type PayrollCycle } from "@/utils/payroll";
import { formatMoney } from "./format";
import { currencyLabel } from "./currency";

const fg = (n: number) => formatMoney(n);

export interface PayrollPdfOptions {
  schoolName: string;
  schoolYear: string;
  scopeLabel: string;
}

/**
 * Génère et télécharge la « Fiche de la masse salariale par cycle » en PDF.
 * Les couleurs sont volontairement fixes (RGB) : un PDF ne peut pas lire les
 * variables de thème CSS.
 */
export function exportPayrollPdf(cycles: PayrollCycle[], options: PayrollPdfOptions): void {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();

  doc.setFillColor(29, 95, 208);
  doc.rect(40, 34, pageWidth - 80, 34, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(`FICHE DE LA MASSE SALARIALE — ${options.schoolName.toUpperCase()}`, pageWidth / 2, 56, {
    align: "center",
  });

  doc.setTextColor(60, 60, 60);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(`Session : ${options.schoolYear}`, 40, 84);
  if (options.scopeLabel) {
    doc.text(options.scopeLabel, pageWidth - 40, 84, { align: "right" });
  }

  const body: string[][] = [];
  const kinds: string[] = [];

  const grandPayroll = cycles.reduce((s, c) => s + c.payroll, 0);
  const grandHours = cycles.reduce((s, c) => s + c.hours, 0);
  const grandCount = cycles.reduce((s, c) => s + c.count, 0);

  cycles.forEach((cycle) => {
    body.push([cycle.id, `Cycle ${cycle.label} — ${cycle.count} enseignant(s) · ${cycle.hours} h / semaine`, "", "", ""]);
    kinds.push("group");
    cycle.teachers.forEach((t, i) => {
      body.push([
        String(i + 1),
        `${t.lastName} ${t.firstName}`,
        String(t.weeklyHours ?? 0),
        t.fixedMonthlySalary ? "Fixe" : fg(t.hourlyRate ?? 0),
        fg(monthlySalary(t)),
      ]);
      kinds.push("item");
    });
    body.push(["", `Sous-total Cycle ${cycle.label}`, String(cycle.hours), "", fg(cycle.payroll)]);
    kinds.push("subtotal");
  });

  const foot = [["", `Masse salariale totale (${grandCount} enseignants)`, `${grandHours} h`, "", fg(grandPayroll)]];
  foot.forEach((row) => {
    body.push(row);
    kinds.push("grand");
  });

  autoTable(doc, {
    startY: 98,
    head: [["N°", "ENSEIGNANT", "Heures / sem.", "Tarif horaire", "Salaire mensuel"]],
    foot,
    body,
    theme: "grid",
    styles: {
      font: "helvetica",
      fontSize: 9,
      cellPadding: 5,
      textColor: [55, 55, 52],
      lineColor: [206, 206, 200],
      lineWidth: 0.5,
    },
    headStyles: {
      fillColor: [234, 238, 245],
      textColor: [55, 60, 55],
      fontStyle: "bold",
      fontSize: 9,
    },
    footStyles: {
      fillColor: [226, 236, 252],
      textColor: [14, 42, 92],
      fontStyle: "bold",
      fontSize: 9,
    },
    columnStyles: {
      0: { cellWidth: 34, halign: "center" },
      1: { cellWidth: "auto" },
      2: { cellWidth: 96, halign: "right" },
      3: { cellWidth: 120, halign: "right" },
      4: { cellWidth: 140, halign: "right" },
    },
    didParseCell: (data: any) => {
      if (data.section !== "body") return;
      const kind = kinds[data.row.index];
      if (kind === "group") {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.fillColor = [243, 245, 240];
      } else if (kind === "subtotal") {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.fillColor = [240, 242, 238];
      }
    },
    didDrawPage: () => {
      const pageHeight = doc.internal.pageSize.getHeight();
      doc.setFontSize(8);
      doc.setTextColor(130, 130, 130);
      doc.text(
        `Salaire mensuel = heures / semaine × tarif horaire × ${WEEKS_PER_MONTH} semaines. Devise : ${currencyLabel()}.`,
        40,
        pageHeight - 20,
      );
    },
  });

  const stamp = new Date().toISOString().slice(0, 10);
  doc.save(`masse-salariale-${options.schoolYear}-${stamp}.pdf`);
}